import { ViewContainerRef } from '@angular/core';
import {
  AbcCommentsListComponent,
  DocumentImagePostType,
  Documentkind,
  DocumentsEditorTableDetailsComponent,
  GalleryButtons,
  ImageViewerComponent,
  MimeType,
  PropertiesEditorFormComponent,
  PropertyType,
} from '@barthauer/abc/common-editors';
import { IPersistable, PersistMode } from '@barthauer/abc/core/data';
import {
  AbcCameraDetailsService,
  AbcDocumentInputComponent,
  AbcHtmlEditorComponent,
  AbcWindowComponent,
  DocumentInputStyle,
  IDocumentInput,
  IWindow,
  ModeDocumentationInput,
  WindowStyle,
  _AbcTextAreaComponent,
} from '@barthauer/abc/ui-components';
import { DataType } from 'devextreme/common';
import { takeUntil } from 'rxjs/operators';
import { LayoutColumnViewModel, LayoutViewModel } from '../../../viewModels/layout.viewModel';
import { DataFormService } from '../../data-form.service';
import { DataFormSettings } from '../settings.model';
import { UiCreatorBase } from './ui-creator-base';

export class SpecialTypeUiCreator extends UiCreatorBase {
  constructor(
    private service: DataFormService,
    private settings: DataFormSettings,
    private abcCameraDetailsService: AbcCameraDetailsService
  ) {
    super(service, settings);
    this.settings.takePicture$.pipe(takeUntil(this.settings.destroyObservables)).subscribe((obj) => {
      this.insertImage(this.settings.layoutViewModel, obj.imageUrl);
    });
  }

  public createPropertiesEditor(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    let containerRef: ViewContainerRef;
    const pef = this.service.arena.loadComponent(PropertiesEditorFormComponent, containerRef);
    this.setTdcolAndRowSpan(td, column);
    const componentInstance: PropertiesEditorFormComponent = <PropertiesEditorFormComponent>pef.componentRef.instance;

    return { domElement: pef.domElement, componentInstance: componentInstance };
  }

  public getPropertyType(type: PropertyType): DataType {
    switch (type) {
      case PropertyType.Number:
        return 'number';
      case PropertyType.Boolean:
        return 'boolean';
      case PropertyType.Picklist:
      case PropertyType.Text:
      default:
        return 'string';
    }
  }

  public createDocumentsEditor(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    let containerRef: ViewContainerRef;
    const iv = this.service.arena.loadComponent(ImageViewerComponent, containerRef);
    this.setTdcolAndRowSpan(td, column);
    const componentInstance: ImageViewerComponent = <ImageViewerComponent>iv.componentRef.instance;
    this.loadLayoutImages();
    componentInstance.messageBoxModel = this.settings.messageBox;
    componentInstance.selectedIndex = this.settings.selectedImageIndex;
    componentInstance.source = this.settings.layoutViewModel.images;
    componentInstance.isDataForm = true;
    componentInstance.onMessageBoxButtonClick.subscribe((choice: string) => {
      this.selectChoiceBox(this.settings.layoutViewModel, choice);
    });
    componentInstance.onUploadButtonClick.subscribe((image) => {
      this.settings.uploadedImage = image;
    });
    componentInstance.onDeleteButtonClick.subscribe((image) => {
      componentInstance.onDialogButtonClick.subscribe(() => {
        this.deleteImage(image);
      });
    });
    return { domElement: iv.domElement, componentInstance: componentInstance };
  }
  public createAbcTextArea(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    this.setTdcolAndRowSpan(td, column);
    const abcTextArea = this.service.abcCoreContainer.createComponent(this.tabsDirective, _AbcTextAreaComponent);
    const abcTextAreaRef: _AbcTextAreaComponent = <_AbcTextAreaComponent>abcTextArea.componentRef.instance;
    abcTextAreaRef.label = column.caption;
    abcTextAreaRef.focusStateEnabled = false;
    abcTextAreaRef.height = '100%';
    abcTextAreaRef.width = '100%';
    abcTextAreaRef.visible = true;
    abcTextAreaRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly ? true : false;
    abcTextAreaRef.onValueChanged.subscribe((result) => {
      this.settings.setCurrentColumnValue(column, result.value);
    });

    this.applyHtmlStyle(abcTextArea.domElement, column);
    this.appendContextMenu(abcTextArea.domElement, column);

    return {
      domElement: abcTextArea.domElement,
      componentInstance: abcTextAreaRef,
    };
  }

  public createAbcHtmlEditor(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    const isReadOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;

    this.setTdcolAndRowSpan(td, column);
    const persistenceCategory = this.service.arena.PERSISTENCE_CATEGORY + 'Html';
    const abcHtmlEditor = this.service.abcContainer.createComponent(this.tabsDirective, AbcHtmlEditorComponent);
    const abcHtmlEditorRef: AbcHtmlEditorComponent = <AbcHtmlEditorComponent>abcHtmlEditor.componentRef.instance;
    abcHtmlEditorRef.label = column.caption;
    abcHtmlEditorRef.visible = true;
    abcHtmlEditorRef.height = 'calc(100% - 17px)';
    abcHtmlEditorRef.width = '100%';
    abcHtmlEditorRef.showIconEdit = !isReadOnly;
    abcHtmlEditorRef.hoverStateEnabled = !isReadOnly;
    abcHtmlEditorRef.focusStateEnabled = !isReadOnly;
    abcHtmlEditorRef.readOnly = isReadOnly;

    abcHtmlEditorRef.onValueChanged.subscribe((result) => {
      if (result.event) {
        abcHtmlEditorRef.value = result.value;
        const text = abcHtmlEditorRef.getPlainText();
        this.settings.setCurrentColumnValue(column, text);
        this.validateColumnValue(column, text);
      }
    });

    abcHtmlEditorRef.onSave.subscribe((value) => {
      const valueFieldName = this.settings.listOfValueWrappers[column.dataField].valueFieldName as string;
      const identifier = valueFieldName.substring(valueFieldName.indexOf("'") + 1, valueFieldName.lastIndexOf("'"));
      if (valueFieldName.indexOf('Comments') >= 0) {
        if (value) {
          /*********  persist comment **********/
          const objectKey: IPersistable = {
            id: null,
            objectType: 'Comment',
            persistenceCategory: persistenceCategory,
            changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
            revertChanges: () => {},
          };
          this.service.persistence.clearPendingObjects(persistenceCategory);
          if (this.settings && this.settings.selectedObject?.data['_' + column.dataField] !== null) {
            objectKey.id = this.settings.selectedObject?.data['_' + column.dataField];
            this.service.persistence.addObjectValue(objectKey, 'Text', abcHtmlEditorRef.getPlainText());
            this.service.persistence.addObjectValue(objectKey, 'Comment', '' + abcHtmlEditorRef.value + '');
            objectKey.changesPersisted(PersistMode.Update);
          } else {
            this.service.persistence.addObjectForMerge(objectKey);
            this.service.persistence.addObjectValue(objectKey, 'Identifier', identifier || 'Standard');
            this.service.persistence.addObjectValue(objectKey, 'SeqNr', 1);
            this.service.persistence.addObjectValue(objectKey, 'Text', abcHtmlEditorRef.getPlainText());
            this.service.persistence.addObjectValue(objectKey, 'Comment', '' + abcHtmlEditorRef.value + '');
          }

          this.service.persistence.addObjectValue(
            objectKey,
            'ParentId',
            this.settings.listOfValueWrappers[column.dataField].objectKey.id
          );
          this.service.persistence.addObjectValue(
            objectKey,
            'ParentObjectType',
            this.settings.listOfValueWrappers[column.dataField].objectKey.objectType
          );
        } else {
          const objectKey: IPersistable = {
            id: this.settings.selectedObject?.data['_' + column.dataField],
            objectType: 'Comment',
            persistenceCategory: persistenceCategory,
            changesPersisted: (persist) => PersistMode.Delete,
          };
          this.service.persistence.addObjectForDelete(objectKey);
        }
        this.service.persistence.persistPendingChanges(persistenceCategory);
        this.service.messageBox.dialog.closeAll();
      } else {
        this.service.messageBox.dialog.closeAll();
        //TODO: PersistPendingChanges
        //this.service.arena.callPersistPendingChanges();
      }
    });

    this.applyHtmlStyle(abcHtmlEditor.domElement, column);
    this.appendContextMenu(abcHtmlEditor.domElement, column);
    //this.settings.setCurrentColumnValue(column, abcHtmlEditorRef.value);

    return {
      domElement: abcHtmlEditor.domElement,
      componentInstance: abcHtmlEditorRef,
    };
  }
  public createCommentsEditor(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    this.setTdcolAndRowSpan(td, column);
    const abcCommonEditors = this.service.arena.loadComponent(AbcCommentsListComponent, td);
    const abcCommonEditorsInstance: AbcCommentsListComponent = <AbcCommentsListComponent>(
      abcCommonEditors.componentRef.instance
    );
    //TODO this should be moved to the binding complete observable in the data form!
    this.settings.setCurrentObjectFunctions.push(async (object) => {
      abcCommonEditorsInstance.oPathFilter = column.oPath?.slice(
        column.oPath?.indexOf('[') + 1,
        column.oPath?.indexOf(']')
      );
      abcCommonEditorsInstance.parentInfo = {
        id: object?.Id,
        objectType: object?.ObjectType,
      };
    });
    return {
      domElement: abcCommonEditors.domElement,
      componentInstance: abcCommonEditorsInstance,
    };
  }

  public createAbcDocumentInput(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    const documentInputStyle: DocumentInputStyle = {
      height: td.height,
      width: '100%',
      disabled: column.isReadOnly || this.settings.layoutViewModel.isReadOnly ? true : false,
    };
    const documentInputAttr: IDocumentInput = {
      id: column.dataField,
      placeholder: column.caption,
      buttonList: [
        {
          name: 'fileInput',
          text: '...',
          type: 'file',
          onClick: (e) => {
            const fileUploadInput = document.createElement('input');
            fileUploadInput.setAttribute('type', 'file');
            fileUploadInput.style.setProperty('display', 'none');
            fileUploadInput.click();
            fileUploadInput.onchange = () => {
              const fileName = fileUploadInput.value.split('\\')[fileUploadInput.value.split('\\').length - 1];
              const fileInput: AbcDocumentInputComponent = <AbcDocumentInputComponent>(
                this.settings.abcComponentInstances.find((element) => element.Key === column.caption).Control
              );
              fileInput.value = fileName;
            };
          },
        },
        {
          name: 'Details',
          text: this.service.arena.res('DataForm-Details'),
          mode: ModeDocumentationInput.Text,
          onClick: (e) => {
            const style: WindowStyle = {
              width: '550px',
              height: '590px',
              // titleColor: "black"
            };
            const WindowAttr: IWindow = {
              id: 'window-Document',
              label: 'Document',
              minimizeButton: false,
              maximizeButton: false,
              component: DocumentsEditorTableDetailsComponent,
              setWindowStyle: (windowStyle: WindowStyle) => (windowStyle = style),
            };
            const window = this.service.abcContainer.createComponent(
              this.tabsDirective,
              AbcWindowComponent,
              WindowAttr
            );
            const windowInstance = <AbcWindowComponent>window.componentRef.instance;
            windowInstance.createComponentInstance().subscribe((activeComponent) => {
              activeComponent.openWindow();
            });
          },
        },
      ],
      setDocumentInputStyle: (style: DocumentInputStyle) => {
        style = documentInputStyle;
      },
    };
    this.setTdcolAndRowSpan(td, column);
    const abcDocumentinput = this.service.abcContainer.createComponent(
      this.tabsDirective,
      AbcDocumentInputComponent,
      documentInputAttr
    );
    const abcDocumentinputRef: AbcDocumentInputComponent = <AbcDocumentInputComponent>(
      abcDocumentinput.componentRef.instance
    );

    abcDocumentinputRef.onChange.subscribe((result) => {
      abcDocumentinputRef.value = result.newValue;
      this.settings.setCurrentColumnValue(column, result.newValue);
    });
    return {
      domElement: abcDocumentinput.domElement,
      componentInstance: abcDocumentinputRef,
    };
  }

  private selectChoiceBox(layout: LayoutViewModel, sourceChoice: string): void {
    sourceChoice = '' + sourceChoice;
    switch (sourceChoice) {
      case GalleryButtons.Kamera: {
        this.takePicture(layout, false);
        break;
      }
      case GalleryButtons.Galerie: {
        this.takePicture(layout, true);
        break;
      }
      case GalleryButtons.Abbrechen: {
        break;
      }
    }
  }
  private async takePicture(layout: LayoutViewModel, useGallery: boolean): Promise<void> {
    if (useGallery) {
      document.getElementById('dataFormUploadImageInput')?.addEventListener('change', (event) => {
        this.uploadImage('', event);
      });
    } else {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        await this.abcCameraDetailsService.openCameraDetails('Camera', (filename, url) =>
          this.insertImage(layout, url)
        );
      }
    }
  }
  private insertImage(layout: LayoutViewModel, imageUrl: any) {
    let editIndex;
    let alreadyExist: boolean;
    const tokenImage = new DocumentImagePostType({
      ParentObjectType: layout.objectType,
      Date: this.prepareDate(false),
      Designation: this.prepareDate(true),
      Object: imageUrl.split(',').pop(),
      Kind: Documentkind.Bild,
    });
    //remove camera placeholder
    layout.images.pop();
    const image = new Image();
    //set image url
    image.src = imageUrl;
    //add image to gallery source
    for (let img of layout.images) {
      if (image.src === img) {
        alreadyExist = true;
      }
    }
    if (!alreadyExist) {
      if (this.settings.uploadedImage === this.settings.placeholderPath) {
        layout.images.splice(0, 0, image.src);
        this.settings.selectedImageIndex = layout.images.indexOf(image.src);
        this.service.cdrSubject.next(null);
      } else {
        this.settings.layoutViewModel.images.forEach((image, index) => {
          if (this.settings.uploadedImage === image) {
            editIndex = index;
          }
        });
        layout.images.splice(editIndex, 1, image.src);
        this.service.documentImages.splice(editIndex - 1, 1);
        this.settings.selectedImageIndex = layout.images.indexOf(image.src);
        this.service.cdrSubject.next(null);
      }
    }
    //add camera placeholder
    layout.images.push(this.settings.placeholderPath);
    if (!alreadyExist) {
      this.service.documentImages.push(tokenImage);
    }
    this.service.persistDocumentImages();
    this.service.persistence.afterPersistPendingChanges$.subscribe(() => {
      layout.images = [];
    });
  }
  private prepareDate(imageDesignation: boolean) {
    const currentDate = new Date();
    if (imageDesignation) {
      return (
        'Image_' +
        currentDate.getFullYear() +
        ('0' + (currentDate.getMonth() + 1)).slice(-2) +
        ('0' + currentDate.getDate()).slice(-2) +
        ('0' + currentDate.getHours()).slice(-2) +
        ('0' + currentDate.getMinutes()).slice(-2) +
        ('0' + currentDate.getSeconds()).slice(-2) +
        '.' +
        MimeType.JPEG
      );
    } else {
      return (
        currentDate.getFullYear() +
        '-' +
        ('0' + (currentDate.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + currentDate.getDate()).slice(-2)
      );
    }
  }
  private uploadImage(imageUrl, event) {
    let reader = new FileReader();
    let file = event.target.files[0];
    reader.addEventListener(
      'load',
      function () {
        imageUrl = reader.result;
      },
      false
    );
    if (file) {
      reader.readAsDataURL(file);
    }
    setTimeout(() => {
      this.insertImage(this.settings.layoutViewModel, imageUrl);
    }, 200);
    this.service.cdrSubject.next(null);
  }

  private deleteImage(imageUrl: string) {
    let deleteIndex;
    if (imageUrl !== this.settings.placeholderPath) {
      this.settings.layoutViewModel.images.forEach((image, index) => {
        if (image === imageUrl) {
          this.settings.layoutViewModel.images.splice(index, 1);
          deleteIndex = index;
        }
      });
      this.service.documentImages.splice(deleteIndex - 1, 1);
    }
  }
}
